import { Plus, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { herdStats, type DomainEvent } from '@dairy/domain';
import { useIsOwner, useSettings } from '@/auth/useSession';
import { Card, Checkbox, EmptyState, Input, PageHeader, Select, buttonVariants } from '@/components/ui';
import { useHerd, useToday } from '@/db/hooks';
import { useAge } from '@/lib/format';
import { ReproBadge, StageBadge } from './common';

/** Screen 6 — Animals list with herd counts (AR-5, AR-7). */
export function AnimalsPage() {
  const { t } = useTranslation();
  const s = useSettings();
  const isOwner = useIsOwner();
  const today = useToday();
  const age = useAge();
  const herd = useHerd(today);
  const [q, setQ] = useState('');
  const [stage, setStage] = useState('');
  const [repro, setRepro] = useState('');
  const [exited, setExited] = useState(false);
  const counts = useMemo(
    () => herdStats(herd.rows.map((r) => r.animal), [...herd.eventsByAnimal.values()].flat() as DomainEvent[], today, s),
    [herd, today, s],
  );
  const rows = herd.rows.filter((r) => {
    if (!exited && r.animal.status !== 'ACTIVE') return false;
    if (stage && r.stage !== stage) return false;
    if (repro && r.repro?.status !== repro) return false;
    const needle = q.trim().toLowerCase();
    return !needle || r.animal.tag.toLowerCase().includes(needle) || (r.animal.name ?? '').toLowerCase().includes(needle);
  });
  return (
    <div className="space-y-4">
      <PageHeader
        title={t('animals.title')}
        actions={
          isOwner ? (
            <Link to="/animals/new" className={buttonVariants({ size: 'sm' })}>
              <Plus className="h-4 w-4" /> {t('animals.add')}
            </Link>
          ) : null
        }
      />
      <Card className="grid grid-cols-3 gap-2 p-3 text-center sm:grid-cols-7" data-testid="herd-counts">
        {(
          [
            ['milking', counts.milking],
            ['dry', counts.dry],
            ['heifers', counts.heifers],
            ['femaleCalves', counts.femaleCalves],
            ['maleCalves', counts.maleCalves],
            ['bulls', counts.bulls],
            ['pregnant', counts.pregnant],
          ] as const
        ).map(([k, v]) => (
          <div key={k} className="rounded-xl bg-stone-100 p-2">
            <div className="text-2xl font-bold tabular-nums">{v}</div>
            <div className="text-xs text-stone-600">{t(`animals.counts.${k}`)}</div>
          </div>
        ))}
      </Card>
      <div className="grid gap-2 sm:grid-cols-[1fr_auto_auto_auto]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-3.5 h-5 w-5 text-stone-400" />
          <Input className="pl-10" placeholder={t('animals.search')} value={q} onChange={(e) => setQ(e.target.value)} aria-label={t('animals.search')} />
        </div>
        <Select value={stage} onChange={(e) => setStage(e.target.value)} aria-label={t('animals.filterStage')}>
          <option value="">{t('animals.allStages')}</option>
          {['MILKING', 'DRY', 'HEIFER', 'CALF', 'BULL'].map((x) => (
            <option key={x} value={x}>
              {t(`animals.stage.${x}`)}
            </option>
          ))}
        </Select>
        <Select value={repro} onChange={(e) => setRepro(e.target.value)} aria-label={t('animals.filterRepro')}>
          <option value="">{t('animals.allRepro')}</option>
          {['OPEN', 'SERVED', 'PREGNANT'].map((x) => (
            <option key={x} value={x}>
              {t(`animals.repro.${x}`)}
            </option>
          ))}
        </Select>
        <Checkbox label={t('animals.showExited')} checked={exited} onChange={(e) => setExited(e.target.checked)} />
      </div>
      {rows.length === 0 && !herd.loading ? <EmptyState>{t('animals.empty')}</EmptyState> : null}
      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={r.animal.id}>
            <Link to={`/animals/${r.animal.id}`} className="flex min-h-16 items-center gap-3 rounded-2xl border border-stone-200 bg-white px-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="truncate font-bold">
                  {r.animal.tag}
                  {r.animal.name ? <span className="ml-2 font-normal text-stone-600">{r.animal.name}</span> : null}
                </div>
                <div className="text-sm text-stone-600">{age(r.animal.birthDate, today)}</div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <StageBadge stage={r.animal.status === 'ACTIVE' ? r.stage : r.animal.status} />
                {r.animal.status === 'ACTIVE' ? <ReproBadge status={r.repro?.status} /> : null}
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
